import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type CSSProperties } from 'react'
import { createPortal } from 'react-dom'
import { useModalA11y } from '@/hooks/useModalA11y'
import { useMediaQuery } from '@/hooks/useMediaQuery'
import k from '@/styles/list-shared.module.css'
import s from './first-run.module.css'

export interface CoachStep {
  /** CSS selector of the element to spotlight; the first visible match wins. */
  target: string
  title: string
  body: string
  /** Where the bubble prefers to sit. It falls back to keep itself on screen. */
  side?: 'bottom' | 'right' | 'top'
}

interface CoachMarksProps {
  steps: CoachStep[]
  onEnd: (completed: boolean) => void
  onStep?: (index: number) => void
}

interface Placement {
  spot: { top: number; left: number; width: number; height: number }
  coach: { top: number; left: number; side: 'bottom' | 'right' | 'top' | 'sheet'; arrowX: number; arrowY: number }
}

const PAD = 6
const GAP = 10
const EDGE = 8
/** The spot's orange ring is drawn 2px outside its box. */
const RING = 2
const SHEET_QUERY = '(max-width: 640px)'

/** On screen horizontally (an off-canvas drawer link is not a target); vertical
    offset is fine because the page can scroll to it. */
export function isOnScreen(el: HTMLElement): boolean {
  const r = el.getBoundingClientRect()
  return r.width > 0 && r.height > 0 && r.right > 0 && r.left < window.innerWidth
}

function findTarget(selector: string): HTMLElement | null {
  const matches = Array.from(document.querySelectorAll<HTMLElement>(selector))
  return matches.find(isOnScreen) ?? null
}

/**
 * Coach marks (design system Components · 09): one step at a time, a
 * spotlight cut from a navy scrim, and a glass bubble beside the target with a
 * step counter, Skip always visible, Back / Next, arrow keys and Escape. Each
 * step is a dialog: focus moves into the bubble on start and returns to the
 * launcher when the tour ends (useModalA11y). The page behind is inert to the
 * pointer only where the scrim is: the spotlit target can still be hovered and
 * clicked. Never mounted on load — a checklist item or Help starts it.
 */
export function CoachMarks({ steps, onEnd, onStep }: CoachMarksProps) {
  const [index, setIndex] = useState(0)
  const [placement, setPlacement] = useState<Placement | null>(null)
  const sheet = useMediaQuery(SHEET_QUERY)
  const reduced = useMediaQuery('(prefers-reduced-motion: reduce)')
  // Stable so the modal contract's effect (focus, scroll lock) runs once per
  // step rather than on every placement update.
  const skip = useCallback(() => onEnd(false), [onEnd])
  const coachRef = useModalA11y<HTMLDivElement>(true, skip)
  const nextRef = useRef<HTMLButtonElement | null>(null)
  const frame = useRef<number | null>(null)
  const step = steps[index]
  const last = index === steps.length - 1

  const measure = useCallback(() => {
    const coach = coachRef.current
    const target = findTarget(step.target)
    if (!coach || !target) return
    const tr = target.getBoundingClientRect()
    const vw = window.innerWidth
    const vh = window.innerHeight
    // Kept inside the window so the ring stays whole around a target that
    // hugs an edge (the search field sits 4px under the top).
    const spotTop = Math.max(RING, tr.top - PAD)
    const spotLeft = Math.max(RING, tr.left - PAD)
    const spot = {
      top: spotTop,
      left: spotLeft,
      width: Math.max(0, Math.min(vw - RING, tr.right + PAD) - spotLeft),
      height: Math.max(0, Math.min(vh - RING, tr.bottom + PAD) - spotTop),
    }
    const cw = coach.offsetWidth
    const ch = coach.offsetHeight
    let side: Placement['coach']['side'] = 'bottom'
    let left = Math.max(EDGE, Math.min(spot.left, vw - cw - EDGE))
    let top = spot.top + spot.height + GAP
    if (sheet) {
      // A bottom sheet, unless the target itself sits in the bottom third (the
      // tab bar): then the sheet rises above it.
      side = 'sheet'
      left = EDGE
      top = spot.top + spot.height > vh * 0.62 ? Math.max(EDGE, spot.top - GAP - ch) : vh - ch - EDGE
    } else if (step.side === 'right' && spot.left + spot.width + GAP + cw <= vw - EDGE) {
      side = 'right'
      left = spot.left + spot.width + GAP
      top = Math.max(EDGE, Math.min(spot.top, vh - ch - EDGE))
    } else if (top + ch > vh - EDGE) {
      side = 'top'
      top = Math.max(EDGE, spot.top - GAP - ch)
    }
    const arrowX = Math.max(12, Math.min(cw - 24, spot.left + 14 - left))
    const arrowY = Math.max(12, Math.min(ch - 24, spot.top + spot.height / 2 - 5 - top))
    setPlacement({ spot, coach: { top, left, side, arrowX, arrowY } })
  }, [coachRef, sheet, step])

  // Bring the target into view, then measure; re-measure on scroll and resize.
  useLayoutEffect(() => {
    const target = findTarget(step.target)
    if (!target) {
      // The page changed under the tour: end it rather than point at nothing.
      onEnd(false)
      return undefined
    }
    const rect = target.getBoundingClientRect()
    const offscreen = rect.top < 72 || rect.bottom > window.innerHeight - 72
    if (offscreen) target.scrollIntoView({ block: 'center', behavior: reduced ? 'auto' : 'smooth' })
    const schedule = () => {
      if (frame.current !== null) return
      frame.current = window.requestAnimationFrame(() => {
        frame.current = null
        measure()
      })
    }
    schedule()
    // A smooth scroll settles over a few frames; measure again once it has.
    const settle = window.setTimeout(schedule, reduced ? 0 : 360)
    window.addEventListener('scroll', schedule, true)
    window.addEventListener('resize', schedule)
    return () => {
      window.clearTimeout(settle)
      window.removeEventListener('scroll', schedule, true)
      window.removeEventListener('resize', schedule)
      if (frame.current !== null) {
        window.cancelAnimationFrame(frame.current)
        frame.current = null
      }
    }
  }, [measure, onEnd, reduced, step])

  useEffect(() => {
    onStep?.(index)
  }, [index, onStep])

  // The primary action takes focus on every step (the modal contract's default
  // is the first control, which here is Skip). The bubble is hidden until it
  // has a placement, and a hidden element cannot take focus, so wait for it.
  const placed = placement !== null
  useEffect(() => {
    if (placed) nextRef.current?.focus({ preventScroll: true })
  }, [index, placed])

  const next = useCallback(() => {
    if (last) onEnd(true)
    else setIndex((i) => Math.min(steps.length - 1, i + 1))
  }, [last, onEnd, steps.length])
  const back = useCallback(() => setIndex((i) => Math.max(0, i - 1)), [])

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'ArrowRight') {
        e.preventDefault()
        next()
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault()
        back()
      }
    },
    [back, next],
  )

  const coachStyle = useMemo<CSSProperties>(() => {
    if (!placement) return { visibility: 'hidden' }
    const { top, left, arrowX, arrowY } = placement.coach
    return { top, left, ['--arrow-x' as string]: `${arrowX}px`, ['--arrow-y' as string]: `${arrowY}px` }
  }, [placement])

  // The scrim's hit area: everything but the spotlight takes the pointer.
  // Until the first measure it covers the whole window.
  const scrimStyle = useMemo<CSSProperties>(() => {
    if (!placement) return {}
    const { top, left, width, height } = placement.spot
    const right = left + width
    const bottom = top + height
    return {
      clipPath: `polygon(evenodd, 0 0, 100% 0, 100% 100%, 0 100%, 0 0, ${left}px ${top}px, ${left}px ${bottom}px, ${right}px ${bottom}px, ${right}px ${top}px, ${left}px ${top}px)`,
    }
  }, [placement])

  // A press on the scrim keeps keyboard focus in the bubble; it used to fall
  // to <body>, where ← → no longer reach the tour.
  const holdFocus = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault()
      if (!coachRef.current?.contains(document.activeElement)) nextRef.current?.focus({ preventScroll: true })
    },
    [coachRef],
  )

  return createPortal(
    <div className={s.tourRoot} data-first-run-tour="" data-coach-marks="" onKeyDown={onKeyDown}>
      <div className={s.scrim} style={scrimStyle} onMouseDown={holdFocus} aria-hidden="true" />
      {/* Mounted once placed, so the scrim's fade-in plays where it shows. */}
      {placement && <div className={s.spot} style={placement.spot} aria-hidden="true" />}
      <div
        key={index}
        ref={coachRef}
        className={s.coach}
        style={coachStyle}
        data-side={placement?.coach.side ?? 'bottom'}
        role="dialog"
        aria-labelledby="first-run-coach-title"
        aria-describedby="first-run-coach-body"
        tabIndex={-1}
      >
        <div className={s.step}>
          <span className={s.stepNum} aria-live="polite">
            {index + 1} of {steps.length}
          </span>
          <span className={s.dots} aria-hidden="true">
            {steps.map((st, i) => (
              <i key={st.target} className={i === index ? s.dotOn : undefined} />
            ))}
          </span>
        </div>
        <h2 id="first-run-coach-title" className={s.coachTitle}>
          {step.title}
        </h2>
        <p id="first-run-coach-body" className={s.coachBody}>
          {step.body}
        </p>
        <div className={s.actions}>
          <button type="button" className={`${k.btn} ${k.btnSurface} ${s.skip}`} onClick={skip}>
            Skip
          </button>
          <button type="button" className={`${k.btn} ${k.btnSecondary}`} onClick={back} disabled={index === 0}>
            Back
          </button>
          <button ref={nextRef} type="button" className={`${k.btn} ${k.btnAlt}`} onClick={next}>
            {last ? 'Done' : 'Next'}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  )
}
